import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import type { DBMeetingSummary, MeetingActionItem, MeetingDecision } from "@/lib/db-types";
import { formatDate } from "@/lib/format";
import { formatOffset, setAllFollowUps } from "@/lib/meeting-summary-calc";
import ActionItemRow from "./ActionItemRow";
import DiscardSummaryDialog from "./DiscardSummaryDialog";

interface SummaryPaneProps {
  summary: DBMeetingSummary;
  /** Verten eller en admin kan redigere og godkjenne et utkast. */
  canEdit: boolean;
  owners: { id: string; full_name: string }[];
  /** Møtet har en kunde, så avtalte punkter kan bli oppfølginger. */
  followUpsAvailable: boolean;
  pending: boolean;
  onJump: (offsetMs: number) => void;
  onApprove: (draft: { summary: string; decisions: MeetingDecision[]; actionItems: MeetingActionItem[] }) => void;
  onDiscard: (reason: string) => void;
  /** Bare når møtet har en transkripsjon å lage på nytt fra. */
  onRegenerate?: () => void;
}

/** Høyre side: redigerbart sammendrag, beslutninger, avtalte punkter og godkjenning. */
export default function SummaryPane({ summary, canEdit, owners, followUpsAvailable, pending, onJump, onApprove, onDiscard, onRegenerate }: SummaryPaneProps) {
  const isDraft = summary.status === "draft";
  const editable = isDraft && canEdit;
  const [text, setText] = useState(summary.summary);
  const [items, setItems] = useState<MeetingActionItem[]>(summary.action_items);
  const [decisions, setDecisions] = useState<MeetingDecision[]>(summary.decisions);
  const [discardOpen, setDiscardOpen] = useState(false);

  return (
    <section aria-label="Referat" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-xl font-semibold">Referat</h2>
        <span className="rounded-pill bg-muted px-2.5 py-0.5 text-xs">
          {summary.status === "approved" ? `Godkjent ${formatDate(summary.approved_at)}` : summary.status === "discarded" ? "Forkastet" : "Utkast"}
          {summary.source === "notes" ? " · fra notater" : ""}
        </span>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="summary-text">Sammendrag</Label>
        <Textarea id="summary-text" value={text} onChange={(e) => setText(e.target.value)} rows={7} disabled={!editable} maxLength={6000} />
      </div>

      {decisions.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-sm font-medium">Beslutninger</h3>
          <ul className="space-y-1 text-sm">
            {decisions.map((d, i) => (
              <li key={i} className="flex items-center gap-2">
                {editable ? (
                  <Input
                    aria-label={`Beslutning ${i + 1}`}
                    className="h-8 flex-1 text-sm"
                    maxLength={500}
                    value={d.text}
                    onChange={(e) => setDecisions(decisions.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                  />
                ) : (
                  <span className="flex-1">{d.text}</span>
                )}
                {d.offset_ms !== null && (
                  <button type="button" className="text-xs underline underline-offset-4" onClick={() => onJump(d.offset_ms!)} aria-label={`Gå til ${formatOffset(d.offset_ms)} i transkripsjonen`}>
                    {formatOffset(d.offset_ms)}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-medium">Avtalte punkter</h3>
            {editable && followUpsAvailable && (
              <Button type="button" size="sm" variant="outline" onClick={() => setItems(setAllFollowUps(items, true))}>
                Legg til alle
              </Button>
            )}
          </div>
          <ul className="space-y-2">
            {items.map((item, i) => (
              <ActionItemRow
                key={item.id}
                item={item}
                owners={owners}
                disabled={!editable}
                followUpsAvailable={followUpsAvailable}
                onChange={(next) => setItems(items.map((it, j) => (j === i ? next : it)))}
                onJump={onJump}
              />
            ))}
          </ul>
        </div>
      )}

      {summary.customer_needs.length > 0 && (
        <div className="space-y-1.5">
          <h3 className="text-sm font-medium">Kundens behov</h3>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            {summary.customer_needs.map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}

      {summary.next_step_suggestion && (
        <p className="text-sm">
          <span className="font-medium">Forslag til neste steg:</span> {summary.next_step_suggestion}
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <Button type="button" disabled={pending || !text.trim()} onClick={() => onApprove({ summary: text, decisions: decisions.filter((d) => d.text.trim()), actionItems: items })}>
            Godkjenn referat
          </Button>
          <Button type="button" variant="outline" disabled={pending} onClick={() => setDiscardOpen(true)}>
            Forkast
          </Button>
          {onRegenerate && (
            <Button type="button" variant="ghost" disabled={pending} onClick={onRegenerate}>
              Lag på nytt
            </Button>
          )}
        </div>
      )}
      <DiscardSummaryDialog open={discardOpen} onOpenChange={setDiscardOpen} pending={pending} onSubmit={(reason) => { onDiscard(reason); setDiscardOpen(false); }} />
    </section>
  );
}
